import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Instagram, Mail, MapPin, Menu, Phone, Users, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const NAV = [["explore", "Explore"], ["journeys", "Journeys"], ["extras", "The Extras"], ["contact", "Contact"]];

export function Wordmark({ light, className }) {
  return (
    <img
      src="/images/topsail-mark.png"
      alt="Topsail Travel"
      data-testid="topsail-logo"
      className={cn("w-auto object-contain", className)}
    />
  );
}

export const PortalLink = ({ className, testId }) => (
  <Link to="/portal" data-testid={testId} className={cn("inline-flex h-10 items-center gap-2 rounded-full border px-5 text-sm font-semibold transition-colors", className)}>
    MyTrip Portal <ArrowRight className="h-4 w-4" />
  </Link>
);

export const FindAgentButton = ({ onClick, className, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={cn("inline-flex h-10 items-center gap-2 rounded-full border px-5 text-sm font-semibold transition-colors", className)}>
    <Users className="h-4 w-4" /> Find an Agent
  </button>
);

export function Header({ onFindAgent }) {
  const [open, setOpen] = useState(false);
  const go = (id) => { setOpen(false); scrollTo(id); };
  return (
    <header className="relative z-20 bg-navy-900">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8 lg:py-6">
        <div className="flex items-center gap-8">
          <button onClick={() => go("top")} aria-label="Topsail Travel home"><Wordmark light className="h-12 sm:h-14" /></button>
          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map(([id, l]) => <button key={id} onClick={() => go(id)} data-testid={`home-nav-${id}`} className="text-sm font-medium text-white/80 transition-colors hover:text-white">{l}</button>)}
          </nav>
        </div>
        <div className="hidden items-center gap-2.5 lg:flex">
          {onFindAgent && <FindAgentButton onClick={onFindAgent} testId="header-find-agent" className="border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white hover:text-navy-900" />}
          <PortalLink testId="header-portal-link" className="border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white hover:text-navy-900" />
        </div>
        <button className="grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white backdrop-blur lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="home-mobile-menu-btn">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="absolute inset-x-0 top-full z-30 mx-4 rounded-2xl bg-white p-3 shadow-xl lg:hidden" data-testid="home-mobile-menu">
          {NAV.map(([id, l]) => <button key={id} onClick={() => go(id)} className="block w-full rounded-xl px-4 py-3 text-left text-base font-medium text-navy-900 hover:bg-slate-50">{l}</button>)}
          {onFindAgent && <FindAgentButton onClick={() => { setOpen(false); onFindAgent(); }} testId="mobile-find-agent" className="mt-2 w-full justify-center border-navy-200 text-navy-900 hover:bg-slate-50" />}
          <PortalLink testId="mobile-portal-link" className="mt-2 w-full justify-center border-navy-900 bg-navy-900 text-white" />
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const socials = [[Instagram, "Instagram", "https://www.instagram.com/topsailtravel/"], [Facebook, "Facebook", "https://www.facebook.com/TopsailTrvl"]];
  return (
    <footer id="contact" className="bg-navy-950 text-white" data-testid="home-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-3 lg:py-20">
        <div>
          <Wordmark light className="h-16" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">Full-service travel planning for cruises, Disney vacations, resorts and journeys designed around you.</p>
          <div className="mt-6 flex gap-2">
            {socials.map(([Icon, l, href]) => <a key={l} href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} aria-label={l} data-testid={`social-${l.toLowerCase()}`} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-white hover:text-white"><Icon className="h-4 w-4" /></a>)}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C9A96E]">Contact</p>
          <ul className="mt-5 space-y-3.5 text-sm text-white/75">
            <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-white/40" />405-662-9638</li>
            <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-white/40" />support@gotopsail.com</li>
            <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-white/40" />Oklahoma City, OK</li>
          </ul>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C9A96E]">Already traveling with us?</p>
          <p className="mt-5 text-sm leading-relaxed text-white/60">Clients can view itineraries, invoices and messages in MyTrip. Advisors sign in through MyCompass.</p>
          <PortalLink testId="footer-portal-link" className="mt-6 border-white/25 text-white hover:bg-white hover:text-navy-900" />
        </div>
      </div>
      <div className="border-t border-white/10">
        <p className="mx-auto max-w-7xl px-5 py-6 text-xs text-white/40 sm:px-8">© {new Date().getFullYear()} Topsail Travel. All rights reserved.</p>
      </div>
    </footer>
  );
}
